import { useState, useCallback, useMemo } from "react";
import { AgGrid } from "@jsq/ds/core";
import { URL_PARAMS } from "../constants";

export interface UseUrlFiltersReturn {
  urlFilterModel: AgGrid.FilterModel | null;
  urlSortModel: AgGrid.SortModelItem[] | null;
  hasUrlFilters: boolean;
  updateUrlFilters: (filterModel: AgGrid.FilterModel | null) => void;
  updateUrlSort: (sortModel: AgGrid.SortModelItem[] | null) => void;
  clearUrlFilters: () => void;
}

const serialize = (data: unknown): string | undefined => {
  if (data === null || data === undefined) return undefined;
  if (Array.isArray(data) && data.length === 0) return undefined;
  if (typeof data === "object" && !Array.isArray(data) && Object.keys(data).length === 0)
    return undefined;
  try {
    return JSON.stringify(data);
  } catch {
    return undefined;
  }
};

const deserialize = <T>(param: string | null): T | null => {
  if (!param) return null;
  try {
    return JSON.parse(param) as T;
  } catch {
    return null;
  }
};

const getUrlParams = (): { filters: string | null; sorts: string | null } => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const filters = urlParams.get(URL_PARAMS.FILTERS_PARAM);
    const sorts = urlParams.get(URL_PARAMS.SORTS_PARAM);
    return { filters, sorts };
  } catch {
    return { filters: null, sorts: null };
  }
};

const setUrlParam = (params: URLSearchParams, key: string, data: unknown): void => {
  const value = serialize(data);
  if (value) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
};

const updateUrlParamsInBrowser = (options: {
  filterModel?: AgGrid.FilterModel | null;
  sortModel?: AgGrid.SortModelItem[] | null;
}): void => {
  try {
    const params = new URLSearchParams(window.location.search);

    if (options.filterModel !== undefined) {
      setUrlParam(params, URL_PARAMS.FILTERS_PARAM, options.filterModel);
    }

    if (options.sortModel !== undefined) {
      setUrlParam(params, URL_PARAMS.SORTS_PARAM, options.sortModel);
    }

    const newSearch = params.toString();
    const currentSearch = window.location.search.replace(/^\?/, "");

    if (newSearch !== currentSearch) {
      const currentHash = window.location.hash;
      const newUrl = newSearch
        ? `${window.location.pathname}?${newSearch}${currentHash}`
        : `${window.location.pathname}${currentHash}`;
      window.history.replaceState({}, "", newUrl);
    }
  } catch {
    // Silently handle URL update failures — the app continues with stale URL params
  }
};

export const useUrlFilters = (): UseUrlFiltersReturn => {
  const [urlFilterModel, setUrlFilterModel] = useState<AgGrid.FilterModel | null>(() => {
    const { filters } = getUrlParams();
    const model = deserialize<AgGrid.FilterModel>(filters);
    if (!model || typeof model !== "object" || Object.keys(model).length === 0) {
      return null;
    }
    return model;
  });

  const [urlSortModel, setUrlSortModel] = useState<AgGrid.SortModelItem[] | null>(() => {
    const { sorts } = getUrlParams();
    const parsed = deserialize<AgGrid.SortModelItem[]>(sorts);
    return Array.isArray(parsed) && parsed.length > 0 ? [parsed[0]] : null;
  });

  const hasUrlFilters = useMemo(() => {
    return urlFilterModel !== null && Object.keys(urlFilterModel).length > 0;
  }, [urlFilterModel]);

  const updateUrlFilters = useCallback((filterModel: AgGrid.FilterModel | null) => {
    updateUrlParamsInBrowser({ filterModel });
    setUrlFilterModel(filterModel && Object.keys(filterModel).length > 0 ? filterModel : null);
  }, []);

  const updateUrlSort = useCallback((sortModel: AgGrid.SortModelItem[] | null) => {
    updateUrlParamsInBrowser({ sortModel });
    setUrlSortModel(sortModel && sortModel.length > 0 ? sortModel : null);
  }, []);

  const clearUrlFilters = useCallback(() => {
    updateUrlParamsInBrowser({ filterModel: null, sortModel: null });
    setUrlFilterModel(null);
    setUrlSortModel(null);
  }, []);

  return {
    urlFilterModel,
    urlSortModel,
    hasUrlFilters,
    updateUrlFilters,
    updateUrlSort,
    clearUrlFilters,
  };
};
